import { createContext, type ReactElement, useContext } from "react";
import { useTranslation } from "react-i18next";
import { type Locale, enUS, sv } from "react-day-picker/locale";

import { LANGUAGE_STORAGE_KEY } from "../i18n";

interface LanguageContextState {
  language: string;
  setLanguage: (language: string) => Promise<void>;
  locale: Locale;
}

const LanguageContext = createContext<LanguageContextState | undefined>(
  undefined,
);

interface Props {
  children: ReactElement;
}

const getLocale = (lang: string): Locale => {
  switch (lang) {
    case "sv":
      return sv;
    case "en":
    default:
      return enUS;
  }
};

export const LanguageProvider = ({ children }: Props) => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const locale = getLocale(language);

  const handleSetLanguage = async (language: string) => {
    await i18n.changeLanguage(language);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        locale,
        setLanguage: handleSetLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error(
      "useLanguageContext must be used within a LanguageProvider",
    );
  }
  return context;
};
